import { useEffect, useRef } from 'react';

/**
 * Custom hook for observing when an element becomes visible in the viewport
 * using the Intersection Observer API.
 *
 * @param {Function} callback - Function to call when the observed element becomes visible
 * @param {Array} dependencies - Array of dependencies for the useEffect hook (defaults to [])
 * @returns {React.RefObject} A ref object that should be attached to the element you want to observe
 *
 * @example
 * // Basic usage - infinite scroll
 * const observerRef = useIntersectionObserver(() => {
 *   loadMoreData();
 * }, [page]);
 *
 * return <div ref={observerRef}>Loading...</div>;
 */
const useIntersectionObserver = (callback, dependencies = []) => {
    const observerRef = useRef(null);

    useEffect(() => {
        // Only create IntersectionObserver on client-side
        if (
            typeof window === 'undefined' ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    callback();
                }
            },
            { threshold: 1.0 }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => observer.disconnect();
    }, dependencies);

    return observerRef;
};

export default useIntersectionObserver;
